import React, { useState, useEffect } from 'react';
import OverlayView from './google-maps/OverlayView';
import { isValidImageURL } from '../lib/media';
import theme from '../themes';

const CustomMarkerOverlay = ({
  position,
  icon,
  onClick,
  id,
  locked,
  isDynamicIcon,
  fillColor = theme.primaryColor,
}) => {
  const [finalIcon, setFinalIcon] = useState(null);
  const [renderAsDefault, setRenderAsDefault] = useState(false);

  useEffect(() => {
    if (isDynamicIcon) {
      // Attempt to load dynamic icon
      setRenderAsDefault(false); // Reset default rendering for dynamic attempts
      const path = `https://${process.env.REACT_APP_WHITELABEL}-assets.socialscavenger.com/pins/generated/task_${id}${locked ? '_locked' : ''}.png`;
      isValidImageURL(path, (isValid) => {
        if (isValid) {
          const newIcon = {
            url: path,
            // Ensure google.maps.Size is only called if window.google.maps exists
            scaledSize: window.google && window.google.maps ? new window.google.maps.Size(26, 32) : { width: 26, height: 32 }
          };
          setFinalIcon(newIcon);
          setRenderAsDefault(false);
        } else {
          setFinalIcon(null);
          setRenderAsDefault(true); // Fallback to default Google marker
        }
      });
    } else {
      // Handle static icon from props
      if (icon && icon.url) {
        isValidImageURL(icon.url, (isValid) => {
          if (isValid) {
            setFinalIcon(icon);
            setRenderAsDefault(false);
          } else {
            setFinalIcon(null);
            setRenderAsDefault(true); // Fallback for bad static icon
          }
        });
      } else {
        // No/invalid static icon provided, or icon prop itself is null/undefined
        setFinalIcon(null);
        setRenderAsDefault(true); // Fallback to default
      }
    }
  }, [isDynamicIcon, id, locked, icon]); // Dependencies for the effect

  if (renderAsDefault) {
    // Render a custom inline SVG marker replicating the default Google marker shape.
    const defaultSize = { width: 28, height: 36 };

    const getPixelPositionOffset = () => ({
      x: -(defaultSize.width / 2),
      y: -defaultSize.height,
    });

    return (
      <OverlayView
        position={position}
        mapPaneName={OverlayView.MAP_PANE}
        getPixelPositionOffset={getPixelPositionOffset}
      >
        <svg
          width={defaultSize.width}
          height={defaultSize.height}
          onClick={onClick}
          style={{ cursor: onClick ? 'pointer' : 'default' }}
        >
          <path 
            stroke={"white"}
            strokeWidth="2" 
            fill={fillColor} 
            d="m14,0.77967c-7.00799,0 -12.67595,5.38996 -12.67595,12.05423c0,9.04067 12.67595,22.38642 12.67595,22.38642s12.67595,-13.34575 12.67595,-22.38642c0,-6.66427 -5.66796,-12.05423 -12.67595,-12.05423z"
          />
          <circle
            fill={"white"}
            r="4.30508"
            cy="12.8339"
            cx="14"
          />
        </svg>
      </OverlayView>
    );
  }

  // If not rendering default, but finalIcon is not ready/valid, render nothing.
  // This prevents rendering OverlayView with invalid/null icon.
  if (!finalIcon || !finalIcon.url) {
    return null; 
  }

  // Logic for OverlayView (custom icon)
  const getIconSize = () => {
    if (finalIcon && finalIcon.scaledSize) {
      // Handles both {width, height} and google.maps.Size-like objects if they have width/height properties
      return {
        width: finalIcon.scaledSize.width,
        height: finalIcon.scaledSize.height,
      };
    }
    return { width: 26, height: 32 }; // Default size for custom icon if somehow not set
  };
  const iconSize = getIconSize();

  const getPixelPositionOffset = (offsetWidth, offsetHeight) => ({
    x: -(iconSize.width / 2),
    y: -iconSize.height,
  });

  return (
    <OverlayView
      position={position}
      mapPaneName={OverlayView.MAP_PANE} // Ensures custom icon is in the pane below polygons
      getPixelPositionOffset={getPixelPositionOffset}
    >
      <div
        onClick={onClick}
        style={{
          backgroundImage: `url(${finalIcon.url})`,
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          width: `${iconSize.width}px`,
          height: `${iconSize.height}px`,
          cursor: onClick ? 'pointer' : 'default',
        }}
      />
    </OverlayView>
  );
};

export default CustomMarkerOverlay; 